import { Link } from '@inertiajs/react'
import { CornerDownRight } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { artifactTimeLabel } from './format'
import type { WorkExecution, WorkerAvatarMap } from './types'

// Recorte curto na home (o log completo por agente vive no portal, aba Rotinas).
const RECENT_LIMIT = 8

interface Props {
  executions?: WorkExecution[]
  workerAvatars?: WorkerAvatarMap
}

// Ponto de status ANTES do nome — SÓ quando há veredito de eval (fleet-eval, que
// AMOSTRA execuções): verde/âmbar/vermelho. Sem veredito, nada — o slot fica
// vazio (mantém os nomes alinhados) mas não inventa um "ok" não verificado.
function StatusDot({ status }: { status: WorkExecution['status'] }) {
  if (!status) return <span className="size-2 flex-none" aria-hidden />

  const tone = status.kind === 'error' ? 'bg-destructive' : status.kind === 'warning' ? 'bg-warning' : 'bg-success'
  const title = status.kind === 'error' ? 'Com erro' : status.kind === 'warning' ? 'Atenção' : 'Concluída'
  return <span className={`size-2 flex-none rounded-full ${tone}`} title={title} aria-label={title} />
}

/**
 * "Últimas execuções" (Iteração 4): a contraparte passada das próximas execuções
 * — sessões-cron REAIS cross-agente (Dashboard::OrgExecutions), mais recentes
 * primeiro. Cada linha = quando · status · rotina (+ processo vinculado, quando
 * há) · agente. É onde processos e rotinas se mesclam: a execução é a unidade
 * comum. Deferida (skeleton próprio). Sem execução, a seção some.
 */
export function RecentRuns({ executions, workerAvatars = {} }: Props) {
  if (!executions) return null

  const recent = executions.slice(0, RECENT_LIMIT)
  if (recent.length === 0) return null

  return (
    <section aria-label="Últimas execuções" className="space-y-2">
      <SectionLabel>Últimas execuções</SectionLabel>
      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {recent.map((execution) => (
            <li
              key={execution.id}
              className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0"
            >
              <span className="w-[84px] flex-none font-mono text-[11px] text-muted-foreground">
                {artifactTimeLabel(execution.last_event_at)}
              </span>
              <StatusDot status={execution.status} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground">{execution.routine_name}</p>
                {execution.process && (
                  <Link
                    href={execution.process.path}
                    className="mt-0.5 flex items-center gap-1 text-[11px] text-brand hover:underline"
                  >
                    <CornerDownRight aria-hidden className="size-3 flex-none" />
                    <span className="truncate">{execution.process.name}</span>
                  </Link>
                )}
              </div>
              <div className="flex flex-none items-center gap-1.5">
                <span className="text-[12px] text-muted-foreground">{execution.worker_name}</span>
                <Avatar src={workerAvatars[execution.worker_id] ?? undefined} alt="" name={execution.worker_name ?? '?'} hueSeed={execution.worker_id} size="xxs" className="flex-none" />
              </div>
            </li>
          )
        )}
      </ul>
    </section>
  )
}

/** Skeleton com a forma da lista (3 linhas) enquanto as execuções chegam. */
export function RecentRunsSkeleton() {
  return (
    <section aria-label="Últimas execuções" className="space-y-2">
      <SectionLabel>Últimas execuções</SectionLabel>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0">
            <div className="h-3 w-[72px] flex-none animate-pulse rounded bg-muted" />
            <div className="size-2 flex-none animate-pulse rounded-full bg-muted" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="h-3 w-2/5 animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-5 w-5 flex-none animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </section>
  )
}
